/**
 * aw26-newsletter-popup.js
 * Newsletter signup pop-up - AW26 (replaces the Seguno popup app).
 *
 * Extends the theme's own <dialog-component> (assets/dialog.js) instead of
 * building a parallel modal system, so Esc-to-close, backdrop click, body
 * scroll lock and the open/close animations are all inherited for free.
 * This file only adds the popup-specific behaviour on top: delay/exit-intent
 * auto-open, localStorage frequency capping, the reopen "teaser" tab, wiring
 * up the theme's {% form 'customer' %} success / error detection to the
 * dialog's open state, and a small local focus-wrap safety net (see
 * #handleFocusTrapKeydown) - dialog.js relies entirely on the native
 * <dialog>.showModal() focus containment (no custom sentinels of its own),
 * and that native wrap can intermittently stop on <body> for one Tab press
 * before landing back inside the dialog.
 *
 * Storage:
 * - localStorage `pb-newsletter-subscribed`      = '1' once the visitor has
 *   signed up. The popup never shows again after that.
 * - localStorage `pb-newsletter-dismissed-until`  = a timestamp (ms). While
 *   in the future, the popup stays hidden and only the teaser tab shows.
 * - sessionStorage `pb-newsletter-popup-submitting` is set the instant this
 *   popup's own form is submitted. `form.posted_successfully?` /
 *   `form.errors` are ambiguous when more than one {% form 'customer' %}
 *   exists on a page (the footer also has a newsletter signup) - this flag
 *   is what lets the popup tell "I was just submitted" apart from "some
 *   other customer form on this page was just submitted".
 */

import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

const SUBSCRIBED_KEY = 'pb-newsletter-subscribed';
const DISMISSED_UNTIL_KEY = 'pb-newsletter-dismissed-until';
const SUBMIT_FLAG_KEY = 'pb-newsletter-popup-submitting';
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const DEFAULT_DELAY_SECONDS = 6;
const DEFAULT_DAYS_HIDDEN = 14;
/** Pause after another dialog closes before the popup may auto-open. */
const AUTO_OPEN_GRACE_MS = 1500;

/**
 * Sequentially-focusable elements, mirroring native Tab order. Used to find
 * the current first/last stop inside the dialog without hardcoding refs per
 * state - the dialog's content differs between the form, error and success
 * states (see #handleFocusTrapKeydown).
 */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Storage access is wrapped defensively: Safari private mode and some
 * privacy extensions can make localStorage/sessionStorage throw on read or
 * write, and that must never break the popup or the page around it.
 *
 * @param {Storage} storage
 * @param {string} key
 * @returns {string | null}
 */
function readStorage(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {Storage} storage
 * @param {string} key
 * @param {string} value
 */
function writeStorage(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch {
    // Ignore - worst case the popup may show again sooner than intended.
  }
}

/**
 * @param {Storage} storage
 * @param {string} key
 */
function removeStorage(storage, key) {
  try {
    storage.removeItem(key);
  } catch {
    // Ignore.
  }
}

function isSubscribed() {
  return readStorage(window.localStorage, SUBSCRIBED_KEY) === '1';
}

function markSubscribed() {
  writeStorage(window.localStorage, SUBSCRIBED_KEY, '1');
  removeStorage(window.localStorage, DISMISSED_UNTIL_KEY);
}

function isDismissedRecently() {
  const until = Number(readStorage(window.localStorage, DISMISSED_UNTIL_KEY));
  return Number.isFinite(until) && until > Date.now();
}

/**
 * @param {number} days
 */
function markDismissedUntil(days) {
  writeStorage(window.localStorage, DISMISSED_UNTIL_KEY, String(Date.now() + days * DAY_IN_MS));
}

/**
 * Consumes (reads then clears) the one-shot "this popup's form was just
 * submitted" flag left behind by #bindSubmitTracking on the previous page.
 *
 * @returns {boolean}
 */
function consumeSubmitFlag() {
  const wasSet = readStorage(window.sessionStorage, SUBMIT_FLAG_KEY) === '1';
  removeStorage(window.sessionStorage, SUBMIT_FLAG_KEY);
  return wasSet;
}

/**
 * A custom element that manages the newsletter signup pop-up: when it
 * auto-opens, how long it stays hidden after a dismiss, and the reopen
 * teaser tab. Dialog mechanics (Esc, scroll lock, animations) are inherited
 * from DialogComponent untouched; focus containment is inherited too, plus
 * a small local wrap-around safety net (#handleFocusTrapKeydown).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element (required by DialogComponent).
 * @property {HTMLButtonElement} [teaserButton] - The reopen tab, absent when the teaser setting is off.
 *
 * @extends {DialogComponent<Refs>}
 */
class NewsletterPopupComponent extends DialogComponent {
  /** @type {number | undefined} */
  #delayTimer;

  /** @type {number | undefined} */
  #graceTimer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    const respondingToOwnSubmit =
      consumeSubmitFlag() && (this.hasAttribute('posted-successfully') || this.hasAttribute('has-errors'));

    if (respondingToOwnSubmit) {
      if (this.hasAttribute('posted-successfully')) markSubscribed();
      this.showDialog();
      return;
    }

    if (isSubscribed()) return;

    this.#bindSubmitTracking();

    if (isDismissedRecently()) {
      this.#revealTeaser();
      return;
    }

    this.#scheduleAutoOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#cancelAutoOpen();
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    // Defensive: normally removed by #handleDialogClose, but guards against
    // disconnection while the dialog happens to still be open.
    this.removeEventListener('keydown', this.#handleFocusTrapKeydown);
  }

  /**
   * Marks this exact popup (as opposed to any other `customer` form on the
   * page, e.g. the footer signup) as the source of the next page load's
   * form response, right before the native submit navigates away.
   */
  #bindSubmitTracking() {
    this.closest('form')?.addEventListener('submit', () => {
      writeStorage(window.sessionStorage, SUBMIT_FLAG_KEY, '1');
    });
  }

  #scheduleAutoOpen() {
    const delaySeconds = Number(this.getAttribute('delay-seconds')) || DEFAULT_DELAY_SECONDS;
    this.#delayTimer = window.setTimeout(this.#triggerAutoOpen, delaySeconds * 1000);

    if (this.hasAttribute('exit-intent') && window.matchMedia('(pointer: fine)').matches) {
      document.documentElement.addEventListener('mouseleave', this.#handleExitIntent);
    }
  }

  #cancelAutoOpen() {
    window.clearTimeout(this.#delayTimer);
    window.clearTimeout(this.#graceTimer);
    document.removeEventListener('close', this.#handleOtherDialogClose, { capture: true });
    document.documentElement.removeEventListener('mouseleave', this.#handleExitIntent);
  }

  /**
   * Exit-intent heuristic: the pointer left the viewport through the top
   * edge (towards the address bar / tab strip), which is only meaningful
   * with a mouse - #scheduleAutoOpen only attaches this on pointer: fine.
   *
   * @param {MouseEvent} event
   */
  #handleExitIntent = (event) => {
    if (event.clientY > 0) return;
    this.#triggerAutoOpen();
  };

  /**
   * Delay and exit-intent race to open the popup; whichever fires first
   * wins and the other trigger is cancelled. While another dialog (cart
   * drawer, free-gift popup, ...) is open the popup stays closed and waits
   * for that dialog to close instead of covering it.
   */
  #triggerAutoOpen = () => {
    this.#cancelAutoOpen();
    if (this.refs.dialog.open) return;

    if (this.#hasOtherDialogOpen()) {
      // `close` does not bubble, so listen in the capture phase on document.
      document.addEventListener('close', this.#handleOtherDialogClose, { capture: true, once: true });
      return;
    }

    this.showDialog();
  };

  /**
   * @returns {boolean} Whether any dialog other than this popup's own is open.
   */
  #hasOtherDialogOpen() {
    return Array.from(document.querySelectorAll('dialog[open]')).some((dialog) => dialog !== this.refs.dialog);
  }

  /**
   * Another dialog closed: wait a short grace so the shopper is not hit by a
   * popup the instant they dismissed something, then re-evaluate (a different
   * dialog may still be open, in which case #triggerAutoOpen arms again).
   */
  #handleOtherDialogClose = () => {
    window.clearTimeout(this.#graceTimer);
    this.#graceTimer = window.setTimeout(this.#triggerAutoOpen, AUTO_OPEN_GRACE_MS);
  };

  #handleDialogOpen = () => {
    // Opened by any route (teaser, own-submit response): a pending retry must not fire later.
    this.#cancelAutoOpen();
    this.refs.teaserButton?.setAttribute('hidden', '');
    this.addEventListener('keydown', this.#handleFocusTrapKeydown);
  };

  #handleDialogClose = () => {
    this.removeEventListener('keydown', this.#handleFocusTrapKeydown);

    if (isSubscribed()) return;
    markDismissedUntil(Number(this.getAttribute('days-hidden')) || DEFAULT_DAYS_HIDDEN);
    this.#revealTeaser();
  };

  #revealTeaser() {
    if (isSubscribed()) return;
    this.refs.teaserButton?.removeAttribute('hidden');
  }

  /**
   * Local safety net for a native <dialog> focus-wrap quirk: tabbing past the
   * last focusable element inside a modal dialog can intermittently move
   * focus to <body> for one stop (seen in Chromium) before the browser's own
   * containment redirects it back inside. dialog.js has no custom focus-trap
   * of its own to patch (it relies solely on native showModal() containment,
   * shared by every dialog in the theme, e.g. cart-drawer), so this performs
   * the same first/last wrap ourselves, deterministically, before the
   * browser's native handling ever gets a chance to race on it. Only active
   * while this dialog is open (added/removed alongside the teaser
   * hide/reveal above).
   *
   * @param {KeyboardEvent} event
   */
  #handleFocusTrapKeydown = (event) => {
    if (event.key !== 'Tab') return;

    const focusable = Array.from(this.refs.dialog.querySelectorAll(FOCUSABLE_SELECTOR));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      /** @type {HTMLElement} */ (last).focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      /** @type {HTMLElement} */ (first).focus();
    }
  };
}

if (!customElements.get('newsletter-popup-component')) {
  customElements.define('newsletter-popup-component', NewsletterPopupComponent);
}
